import { FieldType } from '@components/text-editors/addons/field-mapping';
import { PickLiteral } from '@utils/types';

import { JourneyListItemType, TravellerJourneyActionDetailed } from 'api/types';
import { CreatedBy } from 'api/users/requests';

import { request } from '../request';

type DocumentStatus = 'draft' | 'active';

export type DocumentTemplateFieldTypes = PickLiteral<
  FieldType,
  'email' | 'text' | 'number' | 'date' | 'select' | 'phone' | 'paragraph' | 'file' | 'decimal'
>;

export type DocumentTemplateField = {
  id: string;
  label: string;
  type: DocumentTemplateFieldTypes;
  optionsFrom?: string;
  options?: { label: string; value: string }[];
  dateFormat?: string;
  numberFormat?: string;
  decimalPlaces?: number;
  locale?: string;
};

export type DocumentTemplate = {
  id: string;
  shortId: string;
  name: string;
  description: string | null;
  previewUrl?: string;
  url?: string;
  fieldConfig: DocumentTemplateField[];
  status: DocumentStatus;
  numJourneys: number;
  dateUpdated: Date;
  createdByUser?: CreatedBy;
  exampleData?: { [key: string]: string };
  defaultData?: { [key: string]: string };
  workspace: string;
};

export type DocumentTemplateDetails = DocumentTemplate & { url: string };

export const loadDocumentTemplates = async (queryString?: string) => {
  const response = await request<DocumentTemplate[]>(`v1/document-templates/${queryString ?? ''}`);
  return response.data;
};

export const loadDocumentTemplateJourneys = async (shortId: string) => {
  const response = await request<JourneyListItemType[]>(
    `v1/document-templates/${shortId}/journeys/`
  );
  return response.data;
};

export const loadTemplateDocumentTemplates = async (queryString?: string) => {
  const response = await request<DocumentTemplate[]>(
    `v1/templates/document-templates/${queryString ?? ''}`
  );
  return response.data;
};

export const loadDocumentTemplate = async (shortId: string) => {
  const response = await request<DocumentTemplateDetails>(`v1/document-templates/${shortId}/`);
  return response.data;
};

export const moveDocumentTemplate = async (shortId: string, workspaceId: string) => {
  return await request.post(`v1/document-templates/${shortId}/move/`, { workspace: workspaceId });
};

export const documentTemplatePreview = async (shortId: string, data: Record<string, string>) => {
  const response = await request.post<{ url: string; name: string }>(
    `v1/document-templates/${shortId}/preview/`,
    data
  );
  return response.data;
};
export type CreateUpdateDocumentTemplate = {
  name?: string;
  workspace?: string;
  description?: string | null;
  file?: File | null;
  status?: DocumentStatus;
  fieldConfig?: DocumentTemplateField[];
  defaultData?: Record<string, string>;
};
export const createDocumentTemplate = async (data: CreateUpdateDocumentTemplate) => {
  const response = await request.postForm('v1/document-templates/', data);
  return response.data;
};

export const editDocumentTemplate = async (id: string, data: CreateUpdateDocumentTemplate) => {
  let response = null;
  if (data.file) {
    response = await request.patchForm(`v1/document-templates/${id}/`, data);
  } else {
    response = await request.patch(`v1/document-templates/${id}/`, data);
  }
  return response.data;
};
export const deleteDocumentTemplate = async (id: string) => {
  const response = await request.delete(`v1/document-templates/${id}/`);
  return response.data;
};

export const loadDocument = async (shortId: string) => {
  const response = await request(`v1/documents/${shortId}/document/`);
  return response.data;
};

export const loadConvertedDocument = async (shortId: string) => {
  const response = await request(`v1/documents/${shortId}/converted/`);
  return response.data;
};

export const createJourneyActionDocumentPath = (journeyId: string, actionId: string) =>
  `journey/${journeyId}/actions/${actionId}/documents`;

export const createTravellerJourneyActionDocumentPath = (action: TravellerJourneyActionDetailed) =>
  `journey/${action.journeyId}/traveller/${action.travellerJourneyId}/actions/${action.shortId}/documents`;

export const createJourneyActionFieldPath = (journeyId: string, actionId: string) =>
  `journey/${journeyId}/actions/${actionId}/fields`;

export const createTravellerJourneyActionFieldPath = (action: TravellerJourneyActionDetailed) =>
  `journey/${action.journeyId}/traveller/${action.travellerJourneyId}/actions/${action.shortId}/fields`;

export const createJourneyActionFilePath = (journeyId: string, actionId: string) =>
  `journey/${journeyId}/actions/${actionId}/files`;

export const createTravellerJourneyActionFilePath = (action: TravellerJourneyActionDetailed) =>
  `journey/${action.journeyId}/traveller/${action.travellerJourneyId}/actions/${action.shortId}/files`;
